import { useQuery, useQueryClient } from "@tanstack/react-query";
import { countries } from "../data/countries";
import { pageParams, problemOf, scholarshipsApi } from "../../api/scholarships";

// Shared by the scholarship screens built on the scholarships API (backend scholarships/api)

// Scholarships and loans are separate products on one engine; the stored type tells them apart and
// is fixed when the call is created (COOPERATION: sponsored scholarship, GOVERNMENT: abroad loan).
// Private institutions' loans are lists the institutions send (components/privateLoans)
export const KINDS = {
  scholarship: {
    type: "COOPERATION",
    title: "Scholarships",
    one: "scholarship",
    many: "scholarships",
    listPath: "/scholarshipList",
    applicantsPath: "/applicantsList",
    approvalsPath: "/nominations",
    isLoan: false,
    travels: false,
  },
  loan: {
    type: "GOVERNMENT",
    title: "Abroad loans",
    one: "loan call",
    many: "loan calls",
    listPath: "/loans/abroad",
    applicantsPath: "/loans/abroad/applicants",
    approvalsPath: "/loans/abroad/approvals",
    isLoan: true,
    travels: true,
    // No destination: the student studies wherever they were admitted and says where when applying
    studentChoosesPlace: true,
  },
};
export const kindOf = (type) => Object.values(KINDS).find((k) => k.type === type) ?? KINDS.scholarship;
export const isLoanType = (type) => kindOf(type).isLoan;

// What students are offered: sponsored scholarships; abroad loans come from the Loans service's abroad-loans API now (components/abroadLoans)
const STUDENT_TYPES = [KINDS.scholarship.type];
export const offeredToStudents = (type) => STUDENT_TYPES.includes(type);

export const SCHOLARSHIP_TYPES = [
  { value: "COOPERATION", label: "Partner scholarship" },
  { value: "GOVERNMENT", label: "Abroad loan" },
];
export const TYPE_LABELS = Object.fromEntries(SCHOLARSHIP_TYPES.map((t) => [t.value, t.label]));

export const needsSponsor = (type) => type === KINDS.scholarship.type;

export const STATES = [
  { value: "DRAFT", label: "Draft", color: "gray" },
  { value: "PUBLISHED", label: "Published", color: "green" },
  { value: "CLOSED", label: "Closed", color: "yellow" },
  { value: "DECIDED", label: "Decided", color: "blue" },
  { value: "CANCELLED", label: "Cancelled", color: "red" },
];
export const stateOf = (value) => STATES.find((s) => s.value === value) ?? { value, label: value ?? "—", color: "gray" };

// A private scholarship is only for the students the HEC Admin invites by email
export const VISIBILITIES = [
  { value: "PUBLIC", label: "Public" },
  { value: "PRIVATE", label: "Private (invited students only)" },
];
// Abroad loan calls keep Unlisted: in no list, reached by the link HEC shares
export const CALL_VISIBILITIES = [
  { value: "PUBLIC", label: "Public" },
  { value: "UNLISTED", label: "Private" },
];

export const COUNTRY_OPTIONS = countries.map((c) => ({ value: c.code, label: c.name }));
const COUNTRY_NAMES = Object.fromEntries(countries.map((c) => [c.code, c.name]));
export const countryName = (code) => COUNTRY_NAMES[code] ?? code ?? "—";
// Where an abroad loan student may say they study
export const ABROAD_COUNTRY_OPTIONS = COUNTRY_OPTIONS.filter((c) => c.value !== "RW");

export const hasDestination = (type) => !kindOf(type).studentChoosesPlace;
/** The call's country, or that each student names their own (abroad loans). */
export const destinationOf = (s) =>
  !s?.destinationCountryCode && !hasDestination(s?.type) ? "Student's choice" : countryName(s?.destinationCountryCode);
/** A programme's university and country; abroad loan programmes are study levels without them. */
export const placeOf = (p) => [p?.institutionName, p?.countryCode && countryName(p.countryCode)].filter(Boolean).join(" · ");

export const currencyName = (code) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "currency" }).of(code);
  } catch {
    return code;
  }
};

// A value already saved stays selectable even when Settings no longer offers it
const keeping = (options, value, label) => (value && !options.some((o) => o.value === value) ? [...options, { value, label }] : options);
const byLabel = (a, b) => a.label.localeCompare(b.label);

/** Destination countries offered in Settings, by name; `current` is the scholarship's saved one. */
export const countryOptionsOf = (codes = [], current) =>
  keeping(codes.map((code) => ({ value: code, label: countryName(code) })).sort(byLabel), current, countryName(current));

/** Funding currencies offered in Settings; `current` is a saved one. */
export const currencyOptionsOf = (codes = [], current) =>
  keeping(codes.map((code) => ({ value: code, label: `${code} - ${currencyName(code)}` })), current, current);

/** Academic years a scholarship may take (the current one and the next); `current` keeps a saved one outside them. */
export const yearOptionsOf = (years = [], current) => keeping(years.map((y) => ({ value: y.id, label: y.name })), current?.id, current?.name);

// "First Last"; the email when an account has no name yet (Keycloak users created without one), else "—"
export const fullName = (person) =>
  [person?.firstName, person?.lastName].map((part) => part?.trim()).filter(Boolean).join(" ") || person?.email || "—";

/*
 * Dates. The API speaks UTC instants; HEC staff pick calendar days in Kigali (UTC+2, no daylight
 * saving). A scholarship opens at the start of its first day and closes at the last second of its
 * last day, Kigali time.
 */
const KIGALI = "+02:00";
const instant = (date, time) => (date ? new Date(`${date}T${time}${KIGALI}`).toISOString().replace(".000Z", "Z") : null);
export const opensAtOf = (date) => instant(date, "00:00:00");
export const closesAtOf = (date) => instant(date, "23:59:59");
// The Kigali calendar day of an instant, as YYYY-MM-DD for date inputs
export const kigaliDate = (iso) => (iso ? new Date(new Date(iso).getTime() + 2 * 3600 * 1000).toISOString().slice(0, 10) : "");
export const formatKigaliDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { timeZone: "Africa/Kigali", year: "numeric", month: "short", day: "numeric" }) : "—";
export const todayInKigali = () => kigaliDate(new Date().toISOString());
// "closed 12 Oct 2026" once applications closed (early, or at the closing date), else "closes …"
export const closingOf = (s) => (s.closedAt ? `closed ${formatKigaliDate(s.closedAt)}` : s.closesAt ? `closes ${formatKigaliDate(s.closesAt)}` : "no closing date yet");

// Where a student opens a scholarship directly; the only way into a private one
export const applicationLinkOf = (id) => `${window.location.origin}/scholarships/${id}`;

// Query keys: every scholarship query starts with SCHOLARSHIPS_KEY, so one invalidation refreshes them all
export const SCHOLARSHIPS_KEY = ["scholarships"];
export const OPTIONS_KEY = [...SCHOLARSHIPS_KEY, "options"];

/**
 * What the forms offer, from Settings: the current academic year, the years a new scholarship may
 * take, the destination countries and the funding currencies. Needs no sign-in.
 */
export const useOptions = () =>
  useQuery({
    queryKey: OPTIONS_KEY,
    staleTime: 60 * 1000,
    queryFn: async () => (await scholarshipsApi.get("public/options")).data,
  });
export const staffScholarshipKey = (id) => [...SCHOLARSHIPS_KEY, "staff", "one", id];

// Scholarships open now for the HEC website (no sign-in): published, listed, closing soonest first
export const usePublicScholarships = () =>
  useQuery({
    queryKey: [...SCHOLARSHIPS_KEY, "public"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => (await scholarshipsApi.get("public/scholarships", { params: pageParams({ size: 100 }) })).data?.content ?? [],
  });

// One page of scholarships for the portal; Admin and DG see all, owners their sponsors' published ones
export const useStaffScholarships = (filters) =>
  useQuery({
    queryKey: [...SCHOLARSHIPS_KEY, "staff", "list", filters],
    placeholderData: (previous) => previous,
    queryFn: async () => (await scholarshipsApi.get("staff/scholarships", { params: pageParams(filters) })).data,
  });

// One scholarship with its full set-up, and the ETag its changes must send back
export const useStaffScholarship = (id) =>
  useQuery({
    queryKey: staffScholarshipKey(id),
    enabled: Boolean(id),
    queryFn: () => scholarshipsApi.get(`staff/scholarships/${id}`),
  });

// Sponsor organisations to choose from; HEC has a handful, so one page of the largest size holds them all
export const useSponsors = () =>
  useQuery({
    queryKey: [...SCHOLARSHIPS_KEY, "sponsors"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => (await scholarshipsApi.get("admin/sponsors", { params: { page: 0, size: 100 } })).data?.content ?? [],
  });

// A retired level or field a record already has stays selectable on that record
const withKept = (items, keep, nameKey) => {
  const ids = new Set(items.map((i) => i.id));
  return [...items, ...keep.filter((k) => k?.id && !ids.has(k.id)).map((k) => ({ id: k.id, [nameKey]: k.name }))];
};

/**
 * The education levels new choices may take (Settings; retired ones left out), as { id, levelName }.
 * `keep` adds the record's saved ones ({ id, name }) so a retired level stays selectable there.
 */
export const useEducationLevels = ({ keep = [] } = {}) => {
  const query = useOptions();
  const data = query.data ? withKept(query.data.educationLevels.map((l) => ({ id: l.id, levelName: l.name })), keep, "levelName") : undefined;
  return { ...query, data };
};

/** The fields of study new choices may take, as { id, fieldName }, by name; `keep` as for levels. */
export const useFieldsOfStudy = ({ keep = [] } = {}) => {
  const query = useOptions();
  const data = query.data
    ? withKept(query.data.fieldsOfStudy.map((f) => ({ id: f.id, fieldName: f.name })), keep, "fieldName").sort((a, b) => a.fieldName.localeCompare(b.fieldName))
    : undefined;
  return { ...query, data };
};

// "18 months", "2 years", "2 years 6 months"
export const formatDuration = (months) => {
  if (!months) return "—";
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const part = (n, unit) => (n ? `${n} ${unit}${n === 1 ? "" : "s"}` : "");
  return years && months >= 24 ? [part(years, "year"), part(rest, "month")].filter(Boolean).join(" ") : part(months, "month");
};

// Active sponsors as select options, plus `current` (a scholarship's sponsor) even if it is no longer active
export const sponsorOptionsOf = (sponsors, current) => {
  const options = sponsors.filter((s) => s.active || s.id === current?.id).map((s) => ({ value: s.id, label: s.name }));
  return current && !options.some((o) => o.value === current.id) ? [{ value: current.id, label: current.name }, ...options] : options;
};

// The scholarship's ETag, else the body's `version` in the same form (`"3"` for version 3). Behind
// nginx's gzip the header arrives weak (`W/"3"`); the backend accepts that in If-Match.
const etagOf = (result) => result?.etag ?? (result?.data?.version == null ? null : `"${result.data.version}"`);

/**
 * Runs one set-up change of a scholarship. `change(etag)` sends the request with that ETag in
 * If-Match and returns `{ data, etag }`; the updated scholarship replaces the cached one. A stale
 * ETag (someone else saved first) reloads the scholarship before the error is passed on.
 */
export const useScholarshipChange = (id) => {
  const queryClient = useQueryClient();
  return async (change) => {
    const current = queryClient.getQueryData(staffScholarshipKey(id));
    try {
      const result = await change(etagOf(current));
      queryClient.setQueryData(staffScholarshipKey(id), result);
      queryClient.invalidateQueries({ queryKey: [...SCHOLARSHIPS_KEY, "staff", "list"] });
      return result;
    } catch (error) {
      if (problemOf(error)?.code === "stale-version") await queryClient.invalidateQueries({ queryKey: staffScholarshipKey(id) });
      throw error;
    }
  };
};
